import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  TextField,
  Typography
} from '@mui/material'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useContext, useEffect, useState } from 'react'
import { SnackContext } from 'contexts/SnackContextProvider'
import useDailyOrder from 'hooks/useDailyOrder'
import { OrderContext } from 'contexts/OrderContextProvider'
import { getPickingBackColor } from 'utils'
import { LoadingContext } from 'contexts/LoadingContextProvider'

const DailyOrderPicking = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { orderNumber, company } = useParams()
  const { handleOpenSnackbar } = useContext(SnackContext)
  const { handleScanDailyOrderItem, markDailyOrderAsFinished } =
    useContext(OrderContext)
  const [selectedDailyOrder, setSelectedDailyOrder] = useState<any>(null)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const [scannedValue, setScannedValue] = useState('')
  const { submitPickingDailyOrder, fetchDailyOrder, removeCurrentUserCache } =
    useDailyOrder()
  const { setLoading } = useContext(LoadingContext)
  const returnPath = (location.state as { returnPath?: string } | null)
    ?.returnPath

  const handleBackToPrevious = () => {
    removeCurrentUserCache(`${orderNumber}`, `${company}`)
    if (returnPath) {
      navigate(returnPath)
      return
    }
    navigate(-1)
  }

  useEffect(() => {
    setSelectedDailyOrder(null)
    setInitialLoadComplete(false)
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        await fetchDailyOrder(
          `${company}`,
          `${orderNumber}`,
          order => {
            if (!cancelled) setSelectedDailyOrder(order)
          },
          handleOpenSnackbar
        )
      } finally {
        if (!cancelled) {
          setLoading(false)
          setInitialLoadComplete(true)
        }
      }
    })()
    return () => {
      cancelled = true
      setLoading(false)
    }
  }, [company, orderNumber])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleScanDailyOrderItem(
        scannedValue,
        selectedDailyOrder,
        setScannedValue,
        setSelectedDailyOrder
      )
    }
  }

  if (!initialLoadComplete) {
    return (
      <Button
        sx={{ margin: '20px' }}
        variant='outlined'
        onClick={handleBackToPrevious}
      >
        Back
      </Button>
    )
  }

  if (!selectedDailyOrder || selectedDailyOrder.orderItems.length === 0) {
    return (
      <>
        <Button
          sx={{ margin: '20px' }}
          variant='outlined'
          onClick={handleBackToPrevious}
        >
          Back
        </Button>
        <Box sx={{ margin: '50px' }}>
          <Typography variant='h2' textAlign='center'>
            No order data found
          </Typography>
        </Box>
      </>
    )
  }

  const totalQuantityPicked = selectedDailyOrder.orderItems.reduce(
    (sum: number, item: any) => sum + (item.quantityPicked || 0),
    0
  )
  const allItemsScanned = selectedDailyOrder.orderItems.every(
    (item: any) => Number(item.quantityRemaining) <= 0
  )

  return (
    <Box sx={{ padding: '20px' }} onKeyDown={handleKeyDown}>
      <Grid
        container
        sx={{ marginBottom: '20px' }}
        justifyContent={'space-between'}
      >
        <Grid>
          <Button variant='outlined' onClick={handleBackToPrevious}>
            Cancel
          </Button>
        </Grid>
        <Grid>
          {totalQuantityPicked > 0 && (
            <Button
              variant='contained'
              onClick={() => {
                submitPickingDailyOrder(
                  setLoading,
                  selectedDailyOrder,
                  `${company}`,
                  handleOpenSnackbar,
                  navigate,
                  returnPath,
                  allItemsScanned ? markDailyOrderAsFinished : undefined
                )
              }}
            >
              Submit
            </Button>
          )}
        </Grid>
      </Grid>
      {selectedDailyOrder && (
        <>
          <Typography variant='h5' sx={{ marginBottom: '20px' }}>
            Order: {orderNumber}
          </Typography>

          <TextField
            fullWidth
            label='Enter Product Name or UPC'
            autoFocus
            value={scannedValue}
            onChange={e => setScannedValue(e.target.value)}
            sx={{ marginBottom: '20px' }}
          />

          <List>
            {selectedDailyOrder.orderItems.map((item: any) => (
              <ListItem
                key={item._id}
                sx={{
                  display: 'block',
                  backgroundColor: getPickingBackColor(item),
                  marginBottom: '10px',
                  borderRadius: '8px',
                  padding: '10px'
                }}
              >
                <Grid
                  container
                  justifyContent='space-between'
                  alignItems='center'
                >
                  <Grid>
                    <Typography>{item.name}</Typography>
                    {<Typography>Note: {item.note || 'N/A'}</Typography>}
                    {item.box ? (
                      <Typography sx={{ fontWeight: 600 }}>
                        Box: {item.box}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontWeight: 600 }}>
                        Bin: {item.bin}
                      </Typography>
                    )}
                    {Array.isArray(item.boxCodes) &&
                      item.boxCodes.length > 1 && (
                        <Typography sx={{ fontWeight: 600 }}>
                          Alt Boxes: {item.boxCodes.slice(1).join(', ')}
                        </Typography>
                      )}
                  </Grid>
                  <Grid sx={{ textAlign: 'right' }}>
                    <Typography>Quantity: {item.quantity}</Typography>
                    <Typography>
                      Quantity Remaining: {item.quantityRemaining}
                    </Typography>
                  </Grid>
                </Grid>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  )
}

export default DailyOrderPicking
